import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const CLOUD_FUNCTION_BASE =
  "https://us-central1-mylocalforce-295b8.cloudfunctions.net";
const PLATFORM_FEE_PERCENT = 20; // Platform fee percentage

export default function Refunds() {
  const location = useLocation();
  const navigate = useNavigate();
  const linkedBookingHandledRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("refundable");
  const [processing, setProcessing] = useState({});
  const [approving, setApproving] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [refundReason, setRefundReason] = useState("requested_by_customer");
  const [refundType, setRefundType] = useState("full"); // 'full' or 'partial'

  const loadBookings = async () => {
    setLoading(true);
    try {
      // Load paid bookings and bookings with refund requests
      const bookingsCol = collection(db, "bookings");

      // Get paid bookings
      const q1 = query(bookingsCol, where("paymentStatus", "==", "paid"));
      const snap1 = await getDocs(q1);

      // Get refund requested bookings
      const q2 = query(bookingsCol, where("refundRequested", "==", true));
      const snap2 = await getDocs(q2);

      // Get refunded bookings (fully refunded)
      const q7 = query(bookingsCol, where("paymentStatus", "==", "refunded"));
      const snap7 = await getDocs(q7);

      // Get processing bookings
      const q8 = query(bookingsCol, where("refundProcessing", "==", true));
      const snap8 = await getDocs(q8);

      // Get partially refunded bookings
      const q3 = query(
        bookingsCol,
        where("paymentStatus", "==", "partially_refunded"),
      );
      const snap3 = await getDocs(q3);

      // Get cancelled bookings
      const q4 = query(bookingsCol, where("status", "==", "cancelled"));
      const snap4 = await getDocs(q4);

      // Get rejected bookings
      const q5 = query(bookingsCol, where("status", "==", "rejected"));
      const snap5 = await getDocs(q5);

      // Get not accepted bookings (pending but customer paid and provider didn't accept)
      const q6 = query(bookingsCol, where("providerAccepted", "==", false));
      const snap6 = await getDocs(q6);

      // Get upcoming bookings (provider hasn't responded yet)
      const q9 = query(bookingsCol, where("status", "==", "upcoming"));
      const snap9 = await getDocs(q9);

      const bookingMap = new Map();

      [
        ...snap1.docs,
        ...snap2.docs,
        ...snap3.docs,
        ...snap4.docs,
        ...snap5.docs,
        ...snap6.docs,
        ...snap7.docs,
        ...snap8.docs,
        ...snap9.docs,
      ].forEach((d) => {
        const data = { id: d.id, ...d.data() };

        // Exclude completed bookings - no refunds for completed services
        if (data.status === "completed") {
          return;
        }

        // Include ALL relevant bookings (refunded, processing, paid, etc.)
        // They will be filtered by the status filter dropdown
        const hasPaymentInfo =
          data.chargeId ||
          data.paymentIntentId ||
          data.stripePaymentIntentId ||
          data.stripeSessionId ||
          data.totalAmountPaid ||
          (data.totalAmount && data.totalAmount > 0) ||
          (data.price && data.price > 0);

        if (hasPaymentInfo) {
          bookingMap.set(d.id, data);
        }
      });

      const completedCount = [
        ...snap1.docs,
        ...snap2.docs,
        ...snap3.docs,
        ...snap4.docs,
        ...snap5.docs,
        ...snap6.docs,
        ...snap7.docs,
        ...snap8.docs,
        ...snap9.docs,
      ].filter((d) => d.data().status === "completed").length;

      console.log(
        "Total bookings fetched:",
        snap1.docs.length +
          snap2.docs.length +
          snap3.docs.length +
          snap4.docs.length +
          snap5.docs.length +
          snap6.docs.length +
          snap7.docs.length +
          snap8.docs.length +
          snap9.docs.length,
      );
      console.log("Paid bookings:", snap1.docs.length);
      console.log("Refund requested:", snap2.docs.length);
      console.log("Cancelled bookings:", snap4.docs.length);
      console.log("Rejected bookings:", snap5.docs.length);
      console.log("Not accepted bookings:", snap6.docs.length);
      console.log("Refunded bookings:", snap7.docs.length);
      console.log("Processing bookings:", snap8.docs.length);
      console.log("Upcoming bookings:", snap9.docs.length);
      console.log("Completed bookings (excluded):", completedCount);
      console.log("Bookings after filtering (no completed):", bookingMap.size);

      // Debug: Log first few cancelled/rejected bookings to check paymentStatus
      [...snap4.docs, ...snap5.docs].slice(0, 3).forEach((d) => {
        const data = d.data();
        console.log(
          `Booking ${d.id}: status=${data.status}, paymentStatus=${data.paymentStatus}, price=${data.price || data.totalAmount}`,
        );
      });

      const list = Array.from(bookingMap.values()).sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(0);
        return bTime - aTime;
      });

      setBookings(list);
      setFilteredBookings(list);
    } catch (err) {
      console.error("Error loading bookings for refunds:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    let filtered = bookings;

    // Filter by status
    if (statusFilter === "requested") {
      filtered = filtered.filter(
        (b) =>
          b.refundRequested === true &&
          b.paymentStatus !== "refunded" &&
          b.paymentStatus !== "partially_refunded" &&
          b.refundProcessing !== true,
      );
    } else if (statusFilter === "paid") {
      filtered = filtered.filter(
        (b) =>
          b.paymentStatus === "paid" &&
          !b.refundRequested &&
          b.refundProcessing !== true,
      );
    } else if (statusFilter === "partial") {
      filtered = filtered.filter(
        (b) => b.paymentStatus === "partially_refunded",
      );
    } else if (statusFilter === "cancelled") {
      filtered = filtered.filter(
        (b) =>
          b.status === "cancelled" &&
          b.paymentStatus !== "refunded" &&
          b.paymentStatus !== "partially_refunded" &&
          b.refundProcessing !== true,
      );
    } else if (statusFilter === "rejected") {
      filtered = filtered.filter(
        (b) =>
          b.status === "rejected" &&
          b.paymentStatus !== "refunded" &&
          b.paymentStatus !== "partially_refunded" &&
          b.refundProcessing !== true,
      );
    } else if (statusFilter === "notaccepted") {
      // Include bookings where provider didn't accept OR status is upcoming (not responded yet)
      filtered = filtered.filter(
        (b) =>
          (b.providerAccepted === false || b.status === "upcoming") &&
          b.paymentStatus !== "refunded" &&
          b.paymentStatus !== "partially_refunded" &&
          b.refundProcessing !== true,
      );
    } else if (statusFilter === "processing") {
      filtered = filtered.filter((b) => b.refundProcessing === true);
    } else if (statusFilter === "completed") {
      // Show both fully refunded and partially refunded bookings
      filtered = filtered.filter(
        (b) =>
          b.paymentStatus === "refunded" ||
          b.paymentStatus === "partially_refunded",
      );
    } else if (statusFilter === "refundable") {
      // Only show bookings that can be refunded (exclude processing, fully refunded, and partially refunded)
      filtered = filtered.filter(
        (b) =>
          b.paymentStatus !== "refunded" &&
          b.paymentStatus !== "partially_refunded" &&
          b.refundProcessing !== true,
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (b) =>
          b.customerName?.toLowerCase().includes(term) ||
          b.customerEmail?.toLowerCase().includes(term) ||
          b.serviceName?.toLowerCase().includes(term) ||
          b.id?.toLowerCase().includes(term),
      );
    }

    setFilteredBookings(filtered);
  }, [searchTerm, statusFilter, bookings]);

  const handleOpenDialog = (booking) => {
    setSelectedBooking(booking);
    setRefundReason("requested_by_customer");
    setRefundType("full");
    setDialogOpen(true);
  };

  useEffect(() => {
    if (loading || linkedBookingHandledRef.current) return;

    const linkedBookingId = location.state?.bookingId;
    if (!linkedBookingId) return;

    const targetBooking = bookings.find((booking) => booking.id === linkedBookingId);

    setStatusFilter("all");
    setSearchTerm(linkedBookingId);

    if (targetBooking) {
      handleOpenDialog(targetBooking);
    }

    linkedBookingHandledRef.current = true;
    navigate(location.pathname, { replace: true, state: null });
  }, [loading, bookings, location.state, location.pathname, navigate]);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedBooking(null);
  };

  const handleApproveForRefund = async (booking) => {
    if (!booking) return;
    const bookingId = booking.id;
    setApproving((prev) => ({ ...prev, [bookingId]: true }));

    try {
      // Update booking with admin approval
      await updateDoc(doc(db, "bookings", bookingId), {
        adminApprovedForRefund: true,
        adminApprovedAt: serverTimestamp(),
        adminApprovedBy: "admin", // You can replace with actual admin email/ID
      });

      // Reload bookings to show updated status
      await loadBookings();

      alert("Refund approved! You can now process the refund.");
    } catch (error) {
      console.error("Error approving refund:", error);
      alert("Failed to approve refund: " + error.message);
    } finally {
      setApproving((prev) => ({ ...prev, [bookingId]: false }));
    }
  };

  const handleProcessRefund = async () => {
    if (!selectedBooking) return;

    const bookingId = selectedBooking.id;

    // Debug: Check what payment fields this booking has
    console.log("Processing refund for booking:", {
      id: bookingId,
      chargeId: selectedBooking.chargeId,
      paymentIntentId: selectedBooking.paymentIntentId,
      stripePaymentIntentId: selectedBooking.stripePaymentIntentId,
      stripeChargeId: selectedBooking.stripeChargeId,
      stripeSessionId: selectedBooking.stripeSessionId,
      paymentStatus: selectedBooking.paymentStatus,
      totalAmountPaid: selectedBooking.totalAmountPaid,
      totalAmount: selectedBooking.totalAmount,
      price: selectedBooking.price,
    });

    setProcessing((prev) => ({ ...prev, [bookingId]: true }));

    try {
      const refundAmount =
        refundType === "partial"
          ? calculatePartialRefundAmount(selectedBooking)
          : calculateRefundableAmount(selectedBooking);

      // Call cloud function to process refund
      const resp = await fetch(`${CLOUD_FUNCTION_BASE}/refundBooking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bookingId,
          reason: refundReason,
          adminApproved: true,
          refundType: refundType,
          refundAmount: refundAmount,
        }),
      });

      const json = await resp.json();

      if (json?.success) {
        // Update local state
        setBookings((prev) => prev.filter((b) => b.id !== bookingId));

        // Send email notification
        await sendRefundEmail(selectedBooking, json);

        const refundTypeLabel = json.isPartial
          ? "Partial Refund"
          : "Full Refund";
        const message = json.isPartial
          ? `${refundTypeLabel} processed successfully!\nOriginal: $${json.originalAmount}\nPlatform Fee Deducted: $${(json.originalAmount - json.amount).toFixed(2)}\nRefunded: $${json.amount}`
          : `${refundTypeLabel} processed successfully!\nAmount: $${json.amount}`;

        alert(message);
        handleCloseDialog();
      } else {
        console.error("Refund failed", json);
        const errorMessage = json?.details
          ? `${json.error}\n\n${json.details}`
          : json?.error || "Unknown error";
        alert("Refund failed: " + errorMessage);
      }
    } catch (err) {
      console.error("Refund error", err);
      alert("Refund error: " + err.message);
    } finally {
      setProcessing((prev) => ({ ...prev, [bookingId]: false }));
    }
  };

  const sendRefundEmail = async (booking, refundData) => {
    try {
      // Update booking with email sent flag
      await updateDoc(doc(db, "bookings", booking.id), {
        refundEmailSent: true,
        refundEmailSentAt: serverTimestamp(),
      });

      // You could also call a separate cloud function for email
      // or the refundBooking function should handle email sending
      console.log("Refund email notification sent for booking:", booking.id);
    } catch (err) {
      console.error("Error sending refund email:", err);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) return "N/A";
    return (
      timestamp.toDate().toLocaleDateString() +
      " " +
      timestamp.toDate().toLocaleTimeString()
    );
  };

  const getRefundStatusColor = (booking) => {
    if (booking.paymentStatus === "refunded") return "default";
    if (booking.refundProcessing === true) return "info";
    if (booking.status === "cancelled") return "error";
    if (booking.status === "rejected") return "error";
    if (booking.status === "upcoming") return "warning";
    if (booking.providerAccepted === false) return "warning";
    if (booking.refundRequested) return "warning";
    if (booking.paymentStatus === "partially_refunded") return "info";
    return "success";
  };

  const getRefundStatusLabel = (booking) => {
    if (booking.paymentStatus === "refunded") return "Refund Complete";
    if (booking.refundProcessing === true) return "Refund in Process";
    if (booking.status === "cancelled") return "Cancelled";
    if (booking.status === "rejected") return "Rejected";
    if (booking.status === "upcoming") return "Not Accepted";
    if (booking.providerAccepted === false) return "Not Accepted";
    if (booking.refundRequested) return "Refund Requested";
    if (booking.paymentStatus === "partially_refunded")
      return "Partially Refunded";
    return "Paid";
  };

  const calculateRefundableAmount = (booking) => {
    // Check if already fully refunded
    if (booking.paymentStatus === "refunded" || booking.refundStatus === "completed" || booking.refundedAt) {
      return 0;
    }
    
    const totalPaid =
      booking.totalAmountPaid || booking.totalAmount || booking.price || 0;
    const alreadyRefunded = booking.refund?.amount || booking.refundAmount || booking.refundedAmount || 0;
    return Math.max(0, totalPaid - alreadyRefunded);
  };

  const calculatePartialRefundAmount = (booking) => {
    const fullAmount = calculateRefundableAmount(booking);
    const platformFee = (fullAmount * PLATFORM_FEE_PERCENT) / 100;
    return Math.max(0, fullAmount - platformFee);
  };

  const calculatePlatformFee = (booking) => {
    const fullAmount = calculateRefundableAmount(booking);
    return (fullAmount * PLATFORM_FEE_PERCENT) / 100;
  };

  if (loading)
    return (
      <Box p={3}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box p={1}>
      {/* <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">Refund Management</Typography>
        <IconButton onClick={loadBookings} color="primary" title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box> */}

      {/* Filters */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            placeholder="Search by customer, service, or booking ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Filter"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="refundable">Refundable</MenuItem>
              <MenuItem value="requested">Refund Requested</MenuItem>
              <MenuItem value="cancelled">Cancelled by Customer</MenuItem>
              <MenuItem value="rejected">Rejected by Provider</MenuItem>
              <MenuItem value="notaccepted">Not Accepted by Provider</MenuItem>
              <MenuItem value="paid">Paid Only</MenuItem>
              <MenuItem value="processing">Refund in Process</MenuItem>
              <MenuItem value="partial">Partially Refunded</MenuItem>
              <MenuItem value="completed">Refund Complete</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredBookings.length} of {bookings.length} bookings
          </Typography>
        </Box>
      </Card>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          <strong>Refund Policy:</strong> Only cancelled, rejected, or
          not-accepted bookings are eligible for refunds. Completed bookings
          (where service was successfully delivered) are NOT eligible for
          refunds. Refunds within 24 hours will deduct platform fee. After 24
          hours, full amount may be refunded at admin discretion.
        </Typography>
      </Alert>

      {/* Bookings Grid */}
      {filteredBookings.length === 0 ? (
        <Alert severity="info">
          {bookings.length === 0
            ? "No bookings available for refund."
            : "No bookings match your search criteria."}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredBookings.map((b) => (
            <Grid item size={{ xs: 12, md: 6, lg: 4 }} key={b.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flexGrow: 1, justifyContent: "space-between", display: "flex", flexDirection: "column" }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="start"
                    mb={1}
                  >
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{ fontWeight: 600 }}
                    >
                      {b.serviceName || b.subcategory || "Service"}
                    </Typography>
                    <Chip
                      label={getRefundStatusLabel(b)}
                      color={getRefundStatusColor(b)}
                      size="small"
                    />
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Stack spacing={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {b.customerName || "Unknown Customer"}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2" noWrap>
                        {b.customerEmail || "No email"}
                      </Typography>
                    </Box>

                    {b.location && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2" noWrap>
                          {b.location}
                        </Typography>
                      </Box>
                    )}

                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {formatDate(b.createdAt)}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      <MoneyIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={600}>
                        Paid: $
                        {b.totalAmountPaid || b.totalAmount || b.price || 0}
                      </Typography>
                    </Box>

                    {b.refund && (
                      <Box ml={4}>
                        <Typography variant="body2" color="error">
                          Already Refunded: ${b.refund.amount}
                        </Typography>
                      </Box>
                    )}

                    {/* Only show refundable amount if not refunded, not processing, and not partially refunded */}
                    {b.paymentStatus !== "refunded" &&
                      b.refundStatus !== "completed" &&
                      !b.refundedAt &&
                      b.refundProcessing !== true &&
                      b.paymentStatus !== "partially_refunded" && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="primary"
                          >
                            Refundable: $
                            {calculateRefundableAmount(b).toFixed(2)}
                          </Typography>
                        </Box>
                      )}

                    {b.refundRequested && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          <strong>Customer requested refund</strong>
                          {b.refundReason && `: ${b.refundReason}`}
                        </Typography>
                      </Alert>
                    )}

                    {b.status === "cancelled" && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          <strong>Booking cancelled by customer</strong> -
                          Eligible for refund
                        </Typography>
                      </Alert>
                    )}

                    {b.status === "rejected" && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          <strong>Booking rejected by provider</strong> -
                          Eligible for refund
                          {b.rejectionReason && `: ${b.rejectionReason}`}
                        </Typography>
                      </Alert>
                    )}

                    {b.providerAccepted === false &&
                      b.status !== "rejected" &&
                      b.status !== "upcoming" && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          <Typography variant="caption">
                            <strong>Provider did not accept booking</strong> -
                            Eligible for refund
                          </Typography>
                        </Alert>
                      )}

                    {b.status === "upcoming" && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          <strong>Provider has not responded yet</strong> -
                          Eligible for refund
                        </Typography>
                      </Alert>
                    )}

                    {b.status &&
                      b.status !== "cancelled" &&
                      b.status !== "rejected" &&
                      b.status !== "upcoming" &&
                      b.providerAccepted !== false && (
                        <Typography variant="caption" color="text.secondary">
                          Booking Status: {b.status}
                        </Typography>
                      )}
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  {/* Hide Process Refund button if already refunded, currently processing, or partially refunded */}
                  {b.paymentStatus === "refunded" || b.refundStatus === "completed" || b.refundedAt ? (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Refund Complete</strong> - This booking has been
                        fully refunded
                      </Typography>
                    </Alert>
                  ) : b.refundProcessing === true ? (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Refund in Process</strong> - Please wait...
                      </Typography>
                    </Alert>
                  ) : b.paymentStatus === "partially_refunded" ? (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Partial Refund Complete</strong> - Platform fee
                        deducted, partial refund processed
                      </Typography>
                    </Alert>
                  ) : !b.adminApprovedForRefund ? (
                    <Button
                      variant="contained"
                      color="success"
                      fullWidth
                      onClick={() => handleApproveForRefund(b)}
                      disabled={approving[b.id]}
                    >
                      {approving[b.id] ? "Approving..." : "Approve for Refund"}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color={b.refundRequested ? "warning" : "primary"}
                      fullWidth
                      onClick={() => handleOpenDialog(b)}
                      disabled={
                        processing[b.id] || calculateRefundableAmount(b) <= 0
                      }
                    >
                      {processing[b.id]
                        ? "Processing..."
                        : b.refundRequested
                          ? "Process Refund"
                          : "Process Refund"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Refund Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Refund</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Stack spacing={2} mt={1}>
              <Alert severity="warning">
                This action will process a refund and send an email notification
                to the customer.
              </Alert>

              <Box>
                <Typography variant="subtitle2">Booking Details:</Typography>
                <Typography variant="body2">
                  Customer: {selectedBooking.customerName}
                </Typography>
                <Typography variant="body2">
                  Email: {selectedBooking.customerEmail}
                </Typography>
                <Typography variant="body2">
                  Service:{" "}
                  {selectedBooking.serviceName || selectedBooking.subcategory}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  Total Paid: $
                  {calculateRefundableAmount(selectedBooking).toFixed(2)}
                </Typography>
              </Box>

              <FormControl component="fieldset">
                <Typography variant="subtitle2" mb={1}>
                  Refund Type:
                </Typography>
                <RadioGroup
                  value={refundType}
                  onChange={(e) => setRefundType(e.target.value)}
                >
                  <FormControlLabel
                    value="full"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Full Refund
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Refund entire amount: $
                          {calculateRefundableAmount(selectedBooking).toFixed(
                            2,
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="partial"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Partial Refund (Deduct Platform Fee)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Platform Fee ({PLATFORM_FEE_PERCENT}%): $
                          {calculatePlatformFee(selectedBooking).toFixed(2)}
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          color="primary"
                          fontWeight={600}
                        >
                          Refund Amount: $
                          {calculatePartialRefundAmount(
                            selectedBooking,
                          ).toFixed(2)}
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: refundType === "full" ? "primary.50" : "warning.50",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {refundType === "full"
                    ? "Full Refund Summary"
                    : "Partial Refund Summary"}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Stack spacing={0.5}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Total Paid:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${calculateRefundableAmount(selectedBooking).toFixed(2)}
                    </Typography>
                  </Box>
                  {refundType === "partial" && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="error">
                        Platform Fee ({PLATFORM_FEE_PERCENT}%):
                      </Typography>
                      <Typography
                        variant="body2"
                        color="error"
                        fontWeight={600}
                      >
                        -${calculatePlatformFee(selectedBooking).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Divider />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1" fontWeight={700}>
                      Final Refund Amount:
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      color={refundType === "full" ? "primary" : "warning.main"}
                    >
                      $
                      {refundType === "full"
                        ? calculateRefundableAmount(selectedBooking).toFixed(2)
                        : calculatePartialRefundAmount(selectedBooking).toFixed(
                            2,
                          )}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <FormControl fullWidth>
                <InputLabel>Refund Reason</InputLabel>
                <Select
                  value={refundReason}
                  label="Refund Reason"
                  onChange={(e) => setRefundReason(e.target.value)}
                >
                  <MenuItem value="requested_by_customer">
                    Requested by Customer
                  </MenuItem>
                  <MenuItem value="duplicate">Duplicate Charge</MenuItem>
                  <MenuItem value="fraudulent">Fraudulent</MenuItem>
                </Select>
              </FormControl>
              <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                Note: Stripe only accepts these specific refund reasons for compliance.
              </Alert>

              {selectedBooking.refundRequested && (
                <Alert severity="info">
                  Customer Reason:{" "}
                  {selectedBooking.refundReason || "Not specified"}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            disabled={processing[selectedBooking?.id]}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProcessRefund}
            variant="contained"
            color="warning"
            disabled={processing[selectedBooking?.id]}
          >
            {processing[selectedBooking?.id]
              ? "Processing..."
              : "Confirm Refund"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
